import React, { useLayoutEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

/**
 * The mountain climb — Victor's Figma art (node 61:188) and the two dots on it.
 *
 * Extracted from BeatTheYeti so All Department Metrics can show the SAME tile
 * at a smaller size instead of a lookalike. The vectors are the export, 1:1;
 * only the line colour and the two dot positions change per metric.
 *
 * Scale lives entirely in CSS (.gmbw-mt-chart and whatever a host card
 * overrides it with), so a smaller tile is a smaller box around identical art
 * rather than a second set of paths to keep in step.
 */

// Figma export coordinate space + the exact ridge/line paths.
export const VB_W = 236.101;
export const VB_H = 221.316;
export const LINE_D =
  "M4 126.586C4 126.586 36.9903 53.1835 67.3004 76.3801C97.6105 99.5768 88.6034 31.8496 120.254 57.1709C151.904 82.4922 174 24.5861 174 24.5861";
export const RIDGE_A =
  "M65.5345 76.4424C34.1547 51.4264 0 130.586 0 130.586H176V20.5861C176 20.5861 153.124 83.0338 120.357 55.7266C87.5894 28.4193 96.9144 101.458 65.5345 76.4424Z";
export const RIDGE_B =
  "M177.09 145.536L177.09 66.0749L154.198 67.3529C152.688 67.4372 151.234 67.9473 150.003 68.8242L135.43 79.2028L118.527 87.8429C117.729 88.2509 116.868 88.5218 115.98 88.6444L93.6119 91.7315L88.3048 92.879C86.463 93.2772 84.8209 94.3128 83.6679 95.8033L81.4496 98.6708C79.5684 101.102 76.4603 102.241 73.4536 101.6L70.4493 100.959C68.5866 100.562 66.6434 100.842 64.969 101.75L56.9155 106.115C55.8193 106.709 54.5993 107.039 53.353 107.077L38.271 107.548C37.2112 107.581 36.1686 107.824 35.2038 108.264L24.2149 113.27L21.7971 128.192L58.9702 139.109L85.2543 150.618L177.09 145.536Z";
export const RIDGE_C =
  "M179.5 153.405L179.5 86.1536L179.5 74.2526L164.221 86.1536L145.616 95.6637L121.257 99.0256L112.67 100.086L105.6 110.242L94.4945 107.874C94.4945 107.874 87.2157 113.085 81.8421 114.731C74.7096 116.916 64.5001 113.086 62.75 115.327C60.9999 117.568 49.9581 121.154 49.9581 121.154L16.6368 129.784L85.6659 147.701L112.67 159.526L179.5 153.405Z";

export const GREEN = "#299E47";
export const RED = "#D93D42";

/**
 * The ridge gradients, defined once and referenced by every tile on the page.
 *
 * The ids are global by necessity (SVG `url(#…)` has no scope), so a board
 * carrying both Beat the Yeti and All Department Metrics renders this twice.
 * That is harmless precisely because it is the same markup both times — which
 * is the point of this file. Never fork it into per-card ids.
 */
export const RidgeDefs = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <linearGradient id="gmbwMtnA" x1="92.5" y1="34.586" x2="115.973" y2="108.177" gradientUnits="userSpaceOnUse">
        <stop offset="0.017" stopColor="#DDDDE0" />
        <stop offset="1" stopColor="#EBEBEF" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="gmbwMtnB" x1="123.783" y1="40.831" x2="132.214" y2="163.328" gradientUnits="userSpaceOnUse">
        <stop stopColor="#C1C6E4" />
        <stop offset="0.692" stopColor="#EBECED" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="gmbwMtnC" x1="154.618" y1="44.889" x2="163.281" y2="170.743" gradientUnits="userSpaceOnUse">
        <stop stopColor="#C4C4DD" />
        <stop offset="0.692" stopColor="#EBECED" stopOpacity="0" />
      </linearGradient>
    </defs>
  </svg>
);

/**
 * One climb: the ridges, the line (green ahead / red behind) and the two dots.
 *
 * Dot positions are measured off the real path with getPointAtLength rather
 * than guessed from the fraction — the line is a bezier, so a dot placed by
 * percentage of WIDTH would drift off it wherever the curve is steep.
 */
const MountainClimb = ({ playerFrac, yetiFrac, winning, strokeWidth }) => {
  const lineRef = useRef(null);
  const [dots, setDots] = useState(null);

  useLayoutEffect(() => {
    const path = lineRef.current;
    if (!path || typeof path.getTotalLength !== "function") return;
    const len = path.getTotalLength();
    const p = path.getPointAtLength(playerFrac * len);
    const y = path.getPointAtLength(yetiFrac * len);
    setDots({
      player: { x: (p.x / VB_W) * 100, y: (p.y / VB_H) * 100 },
      yeti: { x: (y.x / VB_W) * 100, y: (y.y / VB_H) * 100 },
    });
  }, [playerFrac, yetiFrac]);

  return (
    <div className="gmbw-mt-chart">
      <svg preserveAspectRatio="none" width="100%" height="100%" viewBox={`0 0 ${VB_W} ${VB_H}`} fill="none">
        <g className="gmbw-mt-ridge">
          <path d={RIDGE_A} fill="url(#gmbwMtnA)" />
          <path d={RIDGE_B} fill="url(#gmbwMtnB)" />
          <path d={RIDGE_C} fill="url(#gmbwMtnC)" />
        </g>
        <path
          ref={lineRef}
          d={LINE_D}
          fill="none"
          stroke={winning ? GREEN : RED}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
        />
      </svg>
      {dots && (
        <>
          <span className="gmbw-mt-dot yeti" style={{ left: `${dots.yeti.x}%`, top: `${dots.yeti.y}%` }} />
          <span className="gmbw-mt-dot player" style={{ left: `${dots.player.x}%`, top: `${dots.player.y}%` }} />
        </>
      )}
    </div>
  );
};

MountainClimb.propTypes = {
  /** 0–1 along the climb line. See pace.js climbFracs. */
  playerFrac: PropTypes.number.isRequired,
  yetiFrac: PropTypes.number.isRequired,
  winning: PropTypes.bool,
  strokeWidth: PropTypes.number,
};
MountainClimb.defaultProps = { winning: false, strokeWidth: 2.379 };

export default MountainClimb;
